/**
 * The second way a launch can reach the layer.
 *
 * The broadcast over the WebSocket is the delivery and stays the fast path. This
 * is the recovery: the backend saves each browser-layer payload before it is
 * broadcast (PlayerEventQueue) and serves what is still current at
 * `GET /player/events/{link}`, so a layer whose socket was down, deaf or not
 * subscribed at that moment can still pick the launch up.
 *
 * Measured on the night of 04-05.10.2026 (Moscow time) on twitch.tv/redpandash: the
 * launches at 00:20, 00:30 and 00:40 never started in the layer, while it beat every
 * three minutes, visible, 1920x1080. The broker did not see an empty channel: when
 * each of those launches was published it reported the channel occupied with two
 * subscribers, exactly as it did for the launches at 00:00, 00:10, 00:59 and 01:09
 * that did start - and for the three that did not, no receipt came back. Neither
 * side could tell that the socket was not delivering, so a fallback that waits for
 * the socket to say it is down would not have fired. This one asks regardless.
 *
 * What this deliberately does not do:
 *  - replay history. A launch whose start window has closed (`startDeadline`,
 *    compared against the server's own clock from the same response) is skipped,
 *    so a layer that wakes up after ten minutes does not play ten minutes of ads;
 *  - decide anything about a launch. Both paths feed the same handler, which also
 *    de-duplicates by launch_id, so a launch that arrives twice plays once;
 *  - schedule from the machine's clock. The saved payload carries the `server_now`
 *    of its publication, which by the time of a poll is stale, and the handler
 *    waits `execute_at - server_now`. The response's own serverTime replaces it,
 *    so the wait is what is left of the schedule rather than all of it again.
 *
 * Nothing here may throw into the page.
 */

export const POLL_INTERVAL_MS = 5000
export const POLL_JITTER_MS = 1000
export const POLL_TIMEOUT_MS = 4000
export const POLL_MAX_BACKOFF_MS = 60000
export const POLL_MAX_RETRY_AFTER_MS = 120000
// 404 is "this streamer is not enabled for the queue". Asked again rarely, so
// that turning it on takes effect without a reload, and a layer that has no queue
// does not keep knocking.
export const POLL_DISABLED_RETRY_MS = 10 * 60 * 1000

const POLLED_EVENT = 'TaskExecuted'

/**
 * @param {Object}   options
 * @param {string}   options.url        the snapshot route for this streamer
 * @param {Function} options.onLaunch   called with each launch payload, once per snapshot it is in
 * @param {Function} [options.fetchImpl]
 * @param {Function} [options.setTimeoutImpl]
 * @param {Function} [options.clearTimeoutImpl]
 * @param {Function} [options.random]
 * @param {Function} [options.AbortControllerImpl]
 */
export function createLaunchPoller (options) {
  const {
    url,
    onLaunch,
    intervalMs = POLL_INTERVAL_MS,
    jitterMs = POLL_JITTER_MS,
    timeoutMs = POLL_TIMEOUT_MS
  } = options
  const fetchImpl = options.fetchImpl || (typeof fetch === 'function' ? fetch.bind(window) : null)
  const setTimeoutImpl = options.setTimeoutImpl || setTimeout
  const clearTimeoutImpl = options.clearTimeoutImpl || clearTimeout
  const random = options.random || Math.random
  const AbortControllerImpl = options.AbortControllerImpl ||
    (typeof AbortController === 'function' ? AbortController : null)

  let timer = null
  let inFlight = false
  let stopped = true
  let failures = 0
  let again = false

  function jitter () {
    return Math.round((random() * 2 - 1) * jitterMs)
  }

  function schedule (delay) {
    if (stopped) return
    if (timer !== null) clearTimeoutImpl(timer)
    timer = setTimeoutImpl(() => {
      timer = null
      poll()
    }, Math.max(0, delay))
  }

  function backoff () {
    return Math.min(intervalMs * Math.pow(2, failures), POLL_MAX_BACKOFF_MS) + jitter()
  }

  function handleSnapshot (snapshot) {
    if (!snapshot || snapshot.schema !== 1 || !Array.isArray(snapshot.events) ||
        typeof snapshot.serverTime !== 'number') {
      throw new Error('unexpected snapshot shape')
    }

    const serverTime = snapshot.serverTime

    for (const item of snapshot.events) {
      if (stopped) return
      if (!item || item.event !== POLLED_EVENT) continue

      const payload = item.payload
      if (!payload || payload.launch_id == null) continue

      const delivery = payload.player_delivery
      // Both numbers are the server's: no skew to allow for.
      if (delivery && typeof delivery.startDeadline === 'number' && delivery.startDeadline <= serverTime) continue

      try {
        onLaunch(Object.assign({}, payload, { server_now: serverTime / 1000 }))
      } catch (e) {
        console.warn('[poll] launch handler failed', e)
      }
    }
  }

  function poll () {
    if (stopped) return
    if (inFlight) {
      again = true
      return
    }
    if (!fetchImpl) return

    inFlight = true
    let controller = null
    let abortTimer = null
    let nextDelay = null

    const settle = () => {
      if (abortTimer !== null) clearTimeoutImpl(abortTimer)
      inFlight = false
      if (again) {
        again = false
        schedule(0)
        return
      }
      schedule(nextDelay === null ? intervalMs + jitter() : nextDelay)
    }

    try {
      const init = { method: 'GET', cache: 'no-store', credentials: 'omit' }
      if (AbortControllerImpl) {
        controller = new AbortControllerImpl()
        init.signal = controller.signal
        abortTimer = setTimeoutImpl(() => { try { controller.abort() } catch (e) { void e } }, timeoutMs)
      }

      Promise.resolve(fetchImpl(url, init))
        .then((response) => {
          if (response.status === 404) {
            failures = 0
            nextDelay = POLL_DISABLED_RETRY_MS
            return null
          }
          if (response.status === 429) {
            failures += 1
            const retryAfter = Number(response.headers && response.headers.get && response.headers.get('Retry-After'))
            nextDelay = retryAfter > 0
              ? Math.min(retryAfter * 1000, POLL_MAX_RETRY_AFTER_MS) + jitter()
              : backoff()
            return null
          }
          if (!response.ok) throw new Error(`poll status ${response.status}`)
          return response.json()
        })
        .then((snapshot) => {
          if (snapshot === null) return
          handleSnapshot(snapshot)
          failures = 0
        })
        .catch((e) => {
          failures += 1
          nextDelay = backoff()
          console.warn('[poll] failed', e && e.message ? e.message : e)
        })
        .then(settle)
    } catch (e) {
      failures += 1
      nextDelay = backoff()
      console.warn('[poll] could not start', e)
      settle()
    }
  }

  return {
    /** Begin polling; the first poll goes out at once. */
    start () {
      if (!stopped) return
      stopped = false
      failures = 0
      again = false
      schedule(0)
    },
    /** Poll now rather than at the next tick: a socket has just come back, or the page has. */
    pollNow () {
      if (stopped) return
      if (inFlight) {
        again = true
        return
      }
      schedule(0)
    },
    stop () {
      stopped = true
      if (timer !== null) clearTimeoutImpl(timer)
      timer = null
      again = false
    },
    isRunning () {
      return !stopped
    }
  }
}

export default createLaunchPoller
